import {useMemo} from 'react'
import {useBabylistEnabled} from '../../../babylist-api'
import {useProductConfiguratorControl} from '../../../contexts/product-configurator'
import {useAddToCart} from '../../../hooks/use-add-to-cart'
import {useCommerceData} from '../../../hooks/use-commerce-data'
import {useNotifyMeModal} from '../../../hooks/use-notify-me-modal'
import {useRedirectLinkPartnerModal} from '../../../hooks/use-redirect-link-partner-modal'
import {isProductOrderable} from '../../../utils/product-utils'

/**
 * XLeap PDP CTA orchestration hook.
 *
 * This hook centralizes CTA behavior (add to cart, notify me, configurator, baby list, partner redirect)
 * so multiple UI surfaces can consume the same action contract (for example cockpit CTA section and sticky bar)
 * while keeping those components mostly presentational.
 *
 * @param {object} params
 * @param {object} params.product
 * @param {boolean} [params.isLoading]
 * @param {Function} [params.onRecommendedProductClick]
 * @param {Function} [params.onBeforeCtaAction]
 * @returns {{
 *   product: object,
 *   isLoading: boolean,
 *   openConfigurator: {onClick: Function},
 *   notifyMe: {modalProps: object, onClick: Function},
 *   addToCart: {onClick: Function},
 *   babyList: {enabled: boolean, onClick: Function},
 *   ecommerceEnabled: boolean,
 *   partnerRedirect: {isAvailable: boolean, redirectProps: object}
 * }}
 */
const useXleapPdpViewModel = ({
    product,
    isLoading: isProductLoading,
    onRecommendedProductClick,
    onBeforeCtaAction
}) => {
    const {handleAddToCart, isLoading} = useAddToCart(product, {
        isLoading: isProductLoading,
        onRecommendedProductClick
    })

    const {startConfigurator} = useProductConfiguratorControl()

    const onAddToCartClick = () => {
        onBeforeCtaAction && onBeforeCtaAction()
        handleAddToCart()
    }

    const redirectProps = useRedirectLinkPartnerModal({product, onClick: onAddToCartClick})
    const {isAvailable} = redirectProps

    const babyListEnabled = useBabylistEnabled()
    const {isEcommerceEnabled} = useCommerceData()
    const {onOpen: onOpenNotifyMeModal, ...notifyMeModalProps} = useNotifyMeModal()

    const productMemo = useMemo(
        () => ({
            ...product,
            isOrderable: isProductOrderable(product)
        }),
        [product]
    )

    return {
        ecommerceEnabled: isEcommerceEnabled,
        product: productMemo,
        isLoading,
        openConfigurator: {
            onClick: () => {
                startConfigurator(product.c_configuratorConfig)
            }
        },
        notifyMe: {
            modalProps: notifyMeModalProps,
            onClick: () => {
                onBeforeCtaAction && onBeforeCtaAction()
                onOpenNotifyMeModal(product)
            }
        },
        addToCart: {
            onClick: () => {
                if (product.c_displayTemplate === 'xpdp-configurator') {
                    startConfigurator(product.c_configuratorConfig)
                } else {
                    onBeforeCtaAction && onBeforeCtaAction()
                    handleAddToCart()
                }
            }
        },
        babyList: {
            enabled: babyListEnabled,
            onClick: () => {
                onBeforeCtaAction && onBeforeCtaAction()
            }
        },
        partnerRedirect: {
            isAvailable,
            redirectProps
        }
    }
}

export default useXleapPdpViewModel
